import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar.js";
import { Icon } from "./Icon.js";
import { useSurface } from "../lib/useSurface.js";

interface ConsoleShellProps {
  /** breadcrumb trail; last entry rendered bold */
  breadcrumb: ReactNode[];
  /** topbar right-aligned actions */
  actions?: ReactNode;
  children?: ReactNode;
}

/** Low-frequency destinations behind もっと見る — drives the "never nowhere" cue on the toggle. */
const DEEP_ROUTES = ["/multilingual", "/studio", "/versions", "/feedback", "/ops", "/billing"];

/**
 * The light "console" management shell. On desktop the bound-ledger sidebar is a pinned rail;
 * at ≤880px it becomes an off-canvas DRAWER (the binder you "open"). It opens/closes by a horizontal
 * flick FROM ANYWHERE (live finger-follow + velocity snap), plus a hamburger, scrim, Esc, scroll-lock
 * and focus trap. The gesture uses native non-passive touch + mouse (NOT Pointer/synthetic events)
 * because on iOS Safari React's root-delegated listeners are passive, so preventDefault is a no-op.
 */
export function ConsoleShell({ breadcrumb, actions, children }: ConsoleShellProps) {
  useSurface("console");
  const [navOpen, setNavOpen] = useState(false);
  const navOpenRef = useRef(navOpen);
  navOpenRef.current = navOpen; // keep the mount-once gesture handler reading the live state
  const loc = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const adminRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // close on route change (skip the first render)
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setNavOpen(false);
  }, [loc.pathname]);

  // if the viewport grows past the drawer tier (e.g. tablet portrait→landscape), drop the open state
  useEffect(() => {
    const mq = window.matchMedia("(min-width:881px)");
    const onChange = () => {
      if (mq.matches) setNavOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // ── Live-follow swipe drawer: NATIVE touch (non-passive touchmove) + mouse; open/close by a horizontal
  //    flick FROM ANYWHERE. preventDefault is a silent no-op inside React synthetic / Pointer handlers on
  //    iOS (root-delegated listeners are passive — WebKit 182521 / React #20999), so touchmove is bound
  //    natively with {passive:false} and preventDefault runs synchronously AFTER the horizontal axis-lock.
  //    translate space: cur in [-W..0]; 0 = open, -W = closed. Snap on release by velocity OR position →
  //    one setNavOpen (so the scroll-lock / focus-trap / close-on-route paths stay untouched).
  useEffect(() => {
    const root = adminRef.current;
    const drawer = drawerRef.current;
    const scrim = scrimRef.current;
    if (!root || !drawer || !scrim) return;

    const EDGE_IGNORE = 16; // leftmost px: leave to the iOS system back-swipe; open from the centre instead
    const SLOP = 8; // px before the axis is decided
    const HYST = 0.52; // position commit ratio (MUI hysteresis)
    const FLING = 0.45; // px/ms fling threshold (~450 px/s, MUI minFlingVelocity)
    const EASE = "cubic-bezier(.22,.61,.36,1)";
    const isMobile = () => window.matchMedia("(max-width:880px)").matches;
    const reduced = () => window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));

    let active = false;
    let bail = false;
    let claimed = false;
    let isTouch = false;
    let startX = 0;
    let startY = 0;
    let startTarget: EventTarget | null = null;
    let baseTx = 0;
    let W = 320;
    let cur = 0;
    let vel = 0;
    let lastX = 0;
    let lastT = 0;
    let raf = 0;

    const flush = () => {
      raf = 0;
      drawer.style.transform = `translate3d(${cur}px,0,0)`;
      const p = clamp(1 + cur / W, 0, 1);
      scrim.style.opacity = String(p);
      scrim.style.pointerEvents = p > 0.01 ? "auto" : "none";
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(flush);
    };

    // horizontally-scrollable ancestors between the finger and the gesture root (MUI getDomTreeShapes)
    const hScrollers = (start: EventTarget | null) => {
      const out: { sl: number; cw: number; sw: number }[] = [];
      let node: HTMLElement | null = start instanceof HTMLElement ? start : null;
      while (node && node !== root) {
        const ox = getComputedStyle(node).overflowX;
        // ONLY real horizontal scroll containers — not elements that merely overflow with
        // overflow:visible (e.g. the hero's giant ▶ ghost), which don't actually scroll.
        if (
          (ox === "auto" || ox === "scroll" || ox === "overlay") &&
          node.clientWidth > 0 &&
          node.scrollWidth > node.clientWidth + 1
        ) {
          out.push({ sl: node.scrollLeft, cw: node.clientWidth, sw: node.scrollWidth });
        }
        node = node.parentElement;
      }
      return out;
    };
    const canScrollH = (list: { sl: number; cw: number; sw: number }[], goingRight: boolean) =>
      list.some((s) => (goingRight ? s.sl + s.cw < s.sw - 1 : s.sl > 0));

    const beginDrag = () => {
      W = drawer.getBoundingClientRect().width || 320; // the only layout read, once per gesture
      baseTx = navOpenRef.current ? 0 : -W;
      cur = baseTx;
      drawer.classList.add("dragging");
      scrim.classList.add("dragging");
      drawer.style.willChange = "transform";
      drawer.style.visibility = "visible"; // a closed drawer is visibility:hidden; reveal for the follow
      schedule();
    };
    const settle = (open: boolean) => {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      drawer.classList.remove("dragging");
      scrim.classList.remove("dragging");
      const dur = reduced() ? "0s" : ".24s";
      drawer.style.transition = `transform ${dur} ${EASE}`;
      scrim.style.transition = `opacity ${dur} ${EASE}`;
      drawer.style.transform = open ? "translate3d(0,0,0)" : `translate3d(${-W}px,0,0)`;
      scrim.style.opacity = open ? "1" : "0";
      scrim.style.pointerEvents = open ? "auto" : "none";
      const clearInline = () => {
        drawer.style.transition = "";
        drawer.style.transform = "";
        drawer.style.visibility = "";
        drawer.style.willChange = "";
        scrim.style.transition = "";
        scrim.style.opacity = "";
        scrim.style.pointerEvents = "";
        drawer.removeEventListener("transitionend", clearInline);
      };
      drawer.addEventListener("transitionend", clearInline);
      window.setTimeout(clearInline, reduced() ? 0 : 360); // safety net if transitionend is missed
      setNavOpen(open); // the ONE state write → drives data-nav-open / scroll-lock / focus-trap
    };
    const reset = () => {
      active = false;
      bail = false;
      claimed = false;
      vel = 0;
    };

    const onStart = (x: number, y: number, target: EventTarget | null, touch: boolean) => {
      if (!isMobile()) {
        active = false;
        bail = true;
        return;
      }
      active = true;
      bail = false;
      claimed = false;
      isTouch = touch;
      startX = lastX = x;
      startY = y;
      lastT = performance.now();
      vel = 0;
      startTarget = target;
      if (x <= EDGE_IGNORE && !navOpenRef.current) bail = true; // leave the iOS edge to the OS back-swipe
    };
    const onMoveCore = (x: number, y: number, ev: Event) => {
      if (!active || bail) return;
      const dx = x - startX;
      const dy = y - startY;
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      if (!claimed) {
        if (ax < SLOP && ay < SLOP) return; // undecided — never preventDefault yet
        if (ay > ax) {
          bail = true; // vertical → native page / .side-inner scroll
          return;
        }
        const goingRight = dx > 0;
        if (navOpenRef.current ? goingRight : !goingRight) {
          bail = true; // wrong direction for the current state
          return;
        }
        if (canScrollH(hScrollers(startTarget), goingRight)) {
          bail = true; // yield to a horizontally-scrollable child (.segmented / wide table / etc.)
          return;
        }
        claimed = true;
        if (reduced()) return; // decide on release; no live follow
        beginDrag();
        startX = x; // re-anchor so the follow starts exactly at the finger
        return;
      }
      if (reduced()) return;
      if (isTouch && ev.cancelable) ev.preventDefault(); // ← the iOS scroll/rubber-band suppression
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) {
        vel = vel * 0.4 + ((x - lastX) / dt) * 0.6; // low-pass velocity (px/ms)
        lastX = x;
        lastT = now;
      }
      cur = clamp(baseTx + (x - startX), -W, 0);
      schedule();
    };
    const onEndCore = (x: number) => {
      if (!active) return;
      if (reduced()) {
        if (claimed) {
          const dx = x - startX;
          if (!navOpenRef.current && dx > 0) setNavOpen(true);
          else if (navOpenRef.current && dx < 0) setNavOpen(false);
        }
        reset();
        return;
      }
      if (!claimed) {
        reset();
        return;
      }
      const progress = clamp(1 + cur / W, 0, 1);
      let open: boolean;
      if (vel > FLING) open = true;
      else if (vel < -FLING) open = false;
      else open = progress >= HYST;
      reset();
      settle(open);
    };

    // touch path (native; touchmove MUST be non-passive so preventDefault works on iOS)
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        active = false;
        bail = true;
        return;
      }
      const t = e.touches[0];
      onStart(t.clientX, t.clientY, e.target, true);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!active || bail) return;
      if (e.touches.length !== 1) {
        bail = true;
        return;
      }
      const t = e.touches[0];
      onMoveCore(t.clientX, t.clientY, e);
    };
    const onTouchEnd = (e: TouchEvent) => {
      onEndCore(e.changedTouches.length ? e.changedTouches[0].clientX : lastX);
    };
    const onTouchCancel = () => {
      const wasClaimed = claimed && !reduced();
      reset();
      if (wasClaimed) settle(navOpenRef.current);
    };

    // mouse path (desktop; document-level move/up so the drag survives leaving .admin; no double-fire)
    let mouseDragging = false;
    const onMouseMove = (e: MouseEvent) => {
      if (mouseDragging) onMoveCore(e.clientX, e.clientY, e);
    };
    const onMouseUp = (e: MouseEvent) => {
      if (!mouseDragging) return;
      mouseDragging = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      onEndCore(e.clientX);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      onStart(e.clientX, e.clientY, e.target, false);
      if (bail || !active) return;
      mouseDragging = true;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: false }); // ← non-passive: the whole iOS fix
    root.addEventListener("touchend", onTouchEnd, { passive: true });
    root.addEventListener("touchcancel", onTouchCancel, { passive: true });
    root.addEventListener("mousedown", onMouseDown);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      root.removeEventListener("touchcancel", onTouchCancel);
      root.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []); // mount once; live open-state read via navOpenRef so listeners never re-bind mid-gesture

  // open: lock background (iOS-safe), make the page inert, trap focus, Esc to close, restore on close
  useEffect(() => {
    if (!navOpen) return;
    restoreRef.current = (document.activeElement as HTMLElement) ?? toggleRef.current;
    document.body.setAttribute("data-nav-open", "");
    pageRef.current?.setAttribute("inert", "");
    const y = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.width = "100%";
    // focus the drawer CONTAINER (tabindex=-1), not the first link — programmatic focus on a link would
    // paint a :focus-visible ring (the yellow/black box on the TEBIKI brand). Tab still traps inside.
    drawerRef.current?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setNavOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = drawerRef.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (!nodes || !nodes.length) return;
      const list = Array.from(nodes).filter((el) => el.offsetParent !== null);
      if (!list.length) return;
      const f = list[0];
      const l = list[list.length - 1];
      if (e.shiftKey && document.activeElement === f) {
        e.preventDefault();
        l.focus();
      } else if (!e.shiftKey && document.activeElement === l) {
        e.preventDefault();
        f.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.removeAttribute("data-nav-open");
      pageRef.current?.removeAttribute("inert");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, y);
      restoreRef.current?.focus?.();
    };
  }, [navOpen]);

  const deep = DEEP_ROUTES.some((p) => loc.pathname.startsWith(p));

  return (
    <div className="admin" ref={adminRef}>
      <Sidebar ref={drawerRef} onNavigate={() => setNavOpen(false)} />
      <div ref={scrimRef} className="nav-scrim" aria-hidden="true" onClick={() => setNavOpen(false)} />
      <div className="main">
        <div className="topbar">
          <button
            ref={toggleRef}
            type="button"
            className="nav-toggle"
            aria-label={navOpen ? "メニューを閉じる" : "メニューを開く"}
            aria-expanded={navOpen}
            aria-controls="console-nav"
            data-deep={deep || undefined}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon name={navOpen ? "close" : "menu"} size={20} />
          </button>
          <span className="breadcrumb">
            {breadcrumb.map((b, i) => (
              <span key={i}>
                {i > 0 ? " / " : null}
                {i === breadcrumb.length - 1 ? <b>{b}</b> : b}
              </span>
            ))}
          </span>
          <span className="grow" />
          {actions}
        </div>
        <div className="page" ref={pageRef}>
          {children}
        </div>
      </div>
    </div>
  );
}

interface PageHeadProps {
  title: ReactNode;
  children?: ReactNode;
}
export function PageHead({ title, children }: PageHeadProps) {
  return (
    <div className="pagehead">
      <h1>{title}</h1>
      {children ? <p className="jp">{children}</p> : null}
    </div>
  );
}
